import React, { useState, useEffect } from 'react';
import { CreditCard, Check, X, Zap, TrendingUp, Rocket, Calendar, AlertCircle, Loader2, Star, Info } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

interface SubscriptionSectionProps {
  userId: string;
  saving: boolean;
  onSavingChange: (saving: boolean) => void;
}

interface Subscription {
  id: string;
  plan_type: 'free' | 'starter' | 'pro' | 'elite';
  status: 'active' | 'cancelled' | 'expired' | 'trial';
  started_at: string;
  expires_at: string | null;
  auto_renew: boolean;
  billing_cycle: 'monthly' | 'yearly';
}

interface PaymentMethod {
  id: string;
  last4: string;
  brand: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
}

const PLANS = [
  {
    id: 'free',
    name: 'Free Trial',
    price: { monthly: 0, yearly: 0 },
    period: '7 days',
    features: [
      '1 Live Trading Bot',
      '1 Paper Trading Bot',
      '1 Backtest per 24h',
      'Basic Trading Signals',
      'Email Support',
      'Community Access'
    ],
    limits: {
      live_bots: 1,
      paper_bots: 1,
      backtests_per_day: 1
    },
    badge: null,
    gradient: 'from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900',
    borderColor: 'border-gray-200 dark:border-gray-700',
    buttonColor: 'bg-gray-600 hover:bg-gray-700'
  },
  {
    id: 'starter',
    name: 'Starter',
    price: { monthly: 19.99, yearly: 199.99 },
    period: 'month',
    features: [
      '1 Live Trading Bot',
      '1 Paper Trading Bot',
      '1 Backtest per 24h',
      'Advanced Trading Strategies',
      'Real-time Market Data',
      'Basic Trading Signals',
      'Email Support',
      'Trading View Integration'
    ],
    limits: {
      live_bots: 1,
      paper_bots: 1,
      backtests_per_day: 1
    },
    badge: null,
    gradient: 'from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20',
    borderColor: 'border-blue-200 dark:border-blue-800',
    buttonColor: 'bg-blue-600 hover:bg-blue-700'
  },
  {
    id: 'pro',
    name: 'Professional',
    price: { monthly: 39.99, yearly: 399.99 },
    period: 'month',
    features: [
      '3 Live Trading Bots',
      '3 Paper Trading Bots',
      '3 Backtests per 24h',
      'All Trading Strategies',
      'Real-time Market Data',
      'Advanced Trading Signals',
      'Priority Email Support',
      'Trading View Integration',
      'Multi-Exchange Support',
      'Custom Indicators'
    ],
    limits: {
      live_bots: 3,
      paper_bots: 3,
      backtests_per_day: 3
    },
    badge: 'Popular',
    gradient: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
    borderColor: 'border-green-200 dark:border-green-800',
    buttonColor: 'bg-green-600 hover:bg-green-700'
  },
  {
    id: 'elite',
    name: 'Elite',
    price: { monthly: 99.99, yearly: 999.99 },
    period: 'month',
    features: [
      '8 Live Trading Bots',
      '8 Paper Trading Bots',
      '8 Backtests per 24h',
      'All Trading Strategies',
      'Real-time Market Data',
      'Premium Trading Signals',
      '24/7 Priority Support',
      'Trading View Integration',
      'Multi-Exchange Support',
      'Custom Indicators',
      'API Access',
      'White-label Options',
      'Dedicated Account Manager'
    ],
    limits: {
      live_bots: 8,
      paper_bots: 8,
      backtests_per_day: 8
    },
    badge: 'Best Value',
    gradient: 'from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20',
    borderColor: 'border-purple-200 dark:border-purple-800',
    buttonColor: 'bg-purple-600 hover:bg-purple-700'
  }
];

export const SubscriptionSection: React.FC<SubscriptionSectionProps> = ({
  userId,
  saving,
  onSavingChange
}) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [showCardModal, setShowCardModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    loadSubscription();
    loadPaymentMethods();
  }, [userId]);

  const loadSubscription = async () => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (!error && data) {
        setSubscription(data);
        setBillingCycle(data.billing_cycle || 'monthly');
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .eq('user_id', userId)
        .order('is_default', { ascending: false });

      if (!error && data) {
        setPaymentMethods(data);
      }
    } catch (error) {
      console.error('Error loading payment methods:', error);
    }
  };

  const handleUpgrade = async (planId: string) => {
    if (planId === 'free') {
      toast.info('Free trial is automatically available for new accounts');
      return;
    }

    onSavingChange(true);
    try {
      const plan = PLANS.find(p => p.id === planId);
      const price = billingCycle === 'yearly' ? plan!.price.yearly : plan!.price.monthly;

      toast.success(`Upgrading to ${plan!.name} - €${price}/${billingCycle}`);

      // TODO: Implement Stripe payment flow
      // For now, simulate subscription creation
      const { error } = await supabase
        .from('subscriptions')
        .upsert({
          user_id: userId,
          plan_type: planId,
          status: 'active',
          billing_cycle: billingCycle,
          auto_renew: true,
          started_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + (billingCycle === 'yearly' ? 365 : 30) * 24 * 60 * 60 * 1000).toISOString()
        });

      if (error) throw error;

      toast.success('Subscription updated successfully');
      loadSubscription();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to upgrade subscription');
    } finally {
      onSavingChange(false);
    }
  };

  const handleCancelSubscription = async () => {
    onSavingChange(true);
    try {
      const { error } = await supabase
        .from('subscriptions')
        .update({
          status: 'cancelled',
          auto_renew: false
        })
        .eq('user_id', userId);

      if (error) throw error;

      toast.success('Subscription cancelled. You can continue using until the end of the billing period.');
      setShowCancelModal(false);
      loadSubscription();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to cancel subscription');
    } finally {
      onSavingChange(false);
    }
  };

  const handleReactivate = async () => {
    onSavingChange(true);
    try {
      const { error } = await supabase
        .from('subscriptions')
        .update({
          status: 'active',
          auto_renew: true
        })
        .eq('user_id', userId);

      if (error) throw error;

      toast.success('Subscription reactivated successfully');
      loadSubscription();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to reactivate subscription');
    } finally {
      onSavingChange(false);
    }
  };

  const currentPlan = PLANS.find(p => p.id === (subscription?.plan_type || 'free'));
  const isCancelled = subscription?.status === 'cancelled';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Subscription & Billing</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Manage your subscription plan and payment methods
        </p>
      </div>

      {/* Current Plan Overview */}
      {subscription && currentPlan && (
        <div className={`bg-gradient-to-br ${currentPlan.gradient} rounded-xl p-6 border ${currentPlan.borderColor}`}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{currentPlan.name} Plan</h3>
                {isCancelled && (
                  <span className="px-3 py-1 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-sm font-medium rounded-full">
                    Cancelled
                  </span>
                )}
                {subscription.status === 'active' && subscription.auto_renew && (
                  <span className="px-3 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-sm font-medium rounded-full">
                    Active
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {subscription.billing_cycle === 'yearly' ? 'Billed Annually' : 'Billed Monthly'}
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-900 dark:text-white">
                €{subscription.billing_cycle === 'yearly' ? currentPlan.price.yearly : currentPlan.price.monthly}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                /{subscription.billing_cycle === 'yearly' ? 'year' : 'month'}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Live Trading Bots</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {currentPlan.limits.live_bots}
              </div>
            </div>
            <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Paper Trading Bots</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {currentPlan.limits.paper_bots}
              </div>
            </div>
            <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Backtests / 24h</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {currentPlan.limits.backtests_per_day}
              </div>
            </div>
          </div>

          {subscription.expires_at && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <span className="text-gray-900 dark:text-white">
                  {isCancelled ? 'Access until:' : 'Next billing date:'}{' '}
                  <strong>{new Date(subscription.expires_at).toLocaleDateString()}</strong>
                </span>
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-4">
            {isCancelled ? (
              <button
                onClick={handleReactivate}
                disabled={saving}
                className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Reactivate Subscription
              </button>
            ) : (
              <button
                onClick={() => setShowCancelModal(true)}
                className="px-6 py-2.5 border border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                Cancel Subscription
              </button>
            )}
          </div>
        </div>
      )}

      {/* Billing Cycle Toggle */}
      <div className="flex items-center justify-center gap-4">
        <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900 dark:text-white' : 'text-gray-500'}`}>
          Monthly
        </span>
        <button
          onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
          className={`relative w-14 h-7 rounded-full transition-colors ${
            billingCycle === 'yearly' ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <div
            className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
              billingCycle === 'yearly' ? 'translate-x-7' : ''
            }`}
          />
        </button>
        <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900 dark:text-white' : 'text-gray-500'}`}>
          Yearly
        </span>
        {billingCycle === 'yearly' && (
          <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-medium rounded-full">
            Save 17%
          </span>
        )}
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {PLANS.map((plan) => {
          const price = billingCycle === 'yearly' ? plan.price.yearly : plan.price.monthly;
          const isCurrentPlan = subscription?.plan_type === plan.id;
          const isPlanHigher = PLANS.findIndex(p => p.id === plan.id) > PLANS.findIndex(p => p.id === (subscription?.plan_type || 'free'));

          return (
            <div
              key={plan.id}
              className={`bg-gradient-to-br ${plan.gradient} rounded-xl p-6 border ${plan.borderColor} ${
                isCurrentPlan ? 'ring-2 ring-offset-2 ring-blue-600 dark:ring-offset-gray-900' : ''
              } relative overflow-hidden`}
            >
              {plan.badge && (
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 text-xs font-medium rounded-full flex items-center gap-1">
                    <Star className="h-3 w-3" />
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">{plan.name}</h3>
                {plan.id === 'free' ? (
                  <div className="mt-2">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">Free</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">for {plan.period}</span>
                  </div>
                ) : (
                  <div className="mt-2">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">€{price}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                  </div>
                )}
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <Check className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleUpgrade(plan.id)}
                disabled={saving || isCurrentPlan || (plan.id === 'free' && subscription !== null)}
                className={`w-full px-6 py-2.5 rounded-lg font-medium transition-colors ${
                  isCurrentPlan
                    ? 'bg-gray-300 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed'
                    : `${plan.buttonColor} text-white disabled:opacity-50`
                }`}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                ) : isCurrentPlan ? (
                  'Current Plan'
                ) : isPlanHigher ? (
                  'Upgrade'
                ) : (
                  'Downgrade'
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Payment Methods */}
      {subscription?.plan_type !== 'free' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600 rounded-lg">
                <CreditCard className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Payment Methods</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Manage your payment cards</p>
              </div>
            </div>
            <button
              onClick={() => setShowCardModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              Add Card
            </button>
          </div>

          {paymentMethods.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <CreditCard className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No payment methods added</p>
            </div>
          ) : (
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <div
                  key={method.id}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <CreditCard className="h-8 w-8 text-gray-500" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {method.brand} •••• {method.last4}
                        {method.is_default && (
                          <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-xs rounded-full">
                            Default
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Expires {method.exp_month}/{method.exp_year}
                      </div>
                    </div>
                  </div>
                  <button className="text-sm text-red-600 dark:text-red-400 hover:underline">
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
                <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Cancel Subscription?</h3>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to cancel your subscription? You'll continue to have access until{' '}
              <strong>{subscription?.expires_at && new Date(subscription.expires_at).toLocaleDateString()}</strong>.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Cancel Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-2">Important Information:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Free trial available for 7 days - no credit card required</li>
              <li>Cancel anytime - no questions asked</li>
              <li>Annual plans save 17% compared to monthly billing</li>
              <li>All plans include TradingView integration</li>
              <li>Upgrade or downgrade your plan at any time</li>
              <li>Secure payments powered by Stripe</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
